'use client';
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Country } from 'country-state-city';
import { ChevronDown, Search, X } from 'lucide-react';

export interface CountryData {
  country_name: string;
  country_iso_code: string;
  phone_country_code: string;
}

interface CountrySelectProps {
  value?: string; // ISO code
  onChange?: (data: CountryData) => void;
  label?: string;
  required?: boolean;
  error?: string;
  className?: string;
}

function getFlagEmoji(isoCode: string): string {
  const codePoints = isoCode
    .toUpperCase()
    .split('')
    .map((char) => 127397 + char.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
}

function getDefaultCountry(): string {
  if (typeof navigator === 'undefined') return 'IN';
  const lang = navigator.language || '';
  const region = lang.split('-')[1];
  if (region && region.length === 2) return region.toUpperCase();
  return 'IN';
}

export default function CountrySelect({
  value,
  onChange,
  label = 'Country',
  required = false,
  error,
  className = '',
}: CountrySelectProps) {
  const allCountries = useMemo(() => Country.getAllCountries(), []);
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<string>(() => value || getDefaultCountry());
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (value) setSelected(value);
  }, [value]);

  useEffect(() => {
    if (isOpen && searchRef.current) {
      searchRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setSearch('');
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return allCountries.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.isoCode.toLowerCase().includes(q) ||
        (c.phonecode && c.phonecode.includes(q))
    );
  }, [allCountries, search]);

  const selectedCountry = allCountries.find((c) => c.isoCode === selected);

  const handleSelect = (isoCode: string) => {
    const country = allCountries.find((c) => c.isoCode === isoCode);
    if (!country) return;
    setSelected(isoCode);
    setIsOpen(false);
    setSearch('');
    onChange?.({
      country_name: country.name,
      country_iso_code: country.isoCode,
      phone_country_code: country.phonecode ? `+${country.phonecode.replace(/\+/g, '')}` : '',
    });
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {label && (
        <label className="block text-sm font-semibold text-foreground mb-1.5">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full flex items-center justify-between px-4 py-2.5 border rounded-xl text-sm bg-card focus:outline-none focus:ring-2 focus:ring-ring transition-all ${
          error ? 'border-red-400' : 'border-input'
        }`}
      >
        <span className="flex items-center gap-2.5 min-w-0">
          {selectedCountry ? (
            <>
              <span className="text-lg leading-none flex-shrink-0">{getFlagEmoji(selectedCountry.isoCode)}</span>
              <span className="text-foreground truncate">{selectedCountry.name}</span>
              <span className="text-muted-foreground text-xs flex-shrink-0">({selectedCountry.isoCode})</span>
            </>
          ) : (
            <span className="text-muted-foreground">Select country...</span>
          )}
        </span>
        <ChevronDown
          size={16}
          className={`text-muted-foreground flex-shrink-0 ml-2 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>
      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}

      {isOpen && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-card border border-border rounded-xl shadow-lg overflow-hidden">
          <div className="p-2 border-b border-border">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                ref={searchRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search country..."
                className="w-full pl-8 pr-8 py-2 text-sm bg-muted rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X size={12} />
                </button>
              )}
            </div>
          </div>
          <div className="max-h-56 overflow-y-auto">
            {filtered.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">No countries found</p>
            ) : (
              filtered.map((country) => (
                <button
                  key={country.isoCode}
                  type="button"
                  onClick={() => handleSelect(country.isoCode)}
                  className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-sm hover:bg-muted transition-colors text-left ${
                    selected === country.isoCode ? 'bg-primary/5 text-primary font-semibold' : 'text-foreground'
                  }`}
                >
                  <span className="text-base leading-none flex-shrink-0">{getFlagEmoji(country.isoCode)}</span>
                  <span className="flex-1 truncate">{country.name}</span>
                  <span className="text-xs text-muted-foreground flex-shrink-0">{country.isoCode}</span>
                  {country.phonecode && (
                    <span className="text-xs text-muted-foreground flex-shrink-0">
                      +{country.phonecode.replace(/\+/g, '')}
                    </span>
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
